import axios from "axios";
import { environment } from "../../../environments/environment";
import { fetchPanelSizeRestrictions } from "./PanelSizeRestrictionsService";

export const getDashboardByWorkstream = async (workstream) => {
  try {
    const accountId = localStorage.getItem("u_account_id");
    // Fetch dashboard data by workstream
    let params = new URLSearchParams();
    params.append("workStream", workstream);
    params.append("accountId", accountId);

    const response = await axios.get(
      environment.getDashboardByWorkstream + params
    );

    return {
      id: response.data?.load?.id,
      tabs: (response.data?.load?.tabs ?? [])
        //order tabs by tabOrder and map to the slice format
        .toSorted((a, b) => a.tabOrder - b.tabOrder)
        .map(
          ({
            id: tabId,
            tabName: label,
            tabOrder,
            accountId,
            tabIcon,
            tabDescription,
            showTitleInUppercase,
          }) => ({
            tabId,
            label,
            tabOrder,
            accountId,
            tabIcon,
            tabDescription,
            showTitleInUppercase,
          })
        ),
    };
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
    throw error; // Re-throw the error for further handling if needed
  }
};

const getContentByPanel = async (panel) => {
  //TODO ask if there is any panel type
  if (!panel.panelType) {
    return JSON.stringify({
      name: "ConfigurePanel",
      props: {},
    });
  }

  if (panel.panelType === "Pie" && panel.datasourceId) {
    try {
      const configResponse = await getPieConfigurationByPanelId(panel.id);
      const dataResponse = await getConsumeDataByPanelId(panel.id);

      const data = dataResponse.data.map((row) => {
        const obj = {};
        dataResponse.columns.forEach((column, index) => {
          obj[column] = row[index];
        });
        return obj;
      });

      return JSON.stringify({
        name: "PieChart",
        props: {
          metadata: configResponse.piePanelDef,
          data,
          showLegend: configResponse.piePanelDef.showLegend || false,
          legendPosition: configResponse.piePanelDef.legendPosition,
          showNumeric: configResponse.piePanelDef.showNumeric,
          showPercentage: configResponse.piePanelDef.showPercentage,
          innerRadius: configResponse.piePanelDef.innerRadius || 0,
          thresholdValues: configResponse.thresholdValues || [],
        },
      });
    } catch (error) {
      console.error(`Error loading pie data for panel ${panel.id}:`, error);
      return JSON.stringify({
        name: "PieChart",
        props: {
          data: [],
          showLegend: true,
        },
      });
    }
  }

  if (panel.panelType === "Table" && panel.datasourceId) {
    try {
      const configResponse = await getTableConfigurationByPanelId(panel.id);
      const dataResponse = await getConsumeDataByPanelId(panel.id);

      // const configuration = configResponse.tableConfig;
      const metadata = configResponse.tableColumns.map((column) => ({
        referencedColumnName: column.referencedColumnName,
        label: column.label,
        specificWidth: column.specificWidth,
        horizontalAlignment: column.horizontalAlignment,
        useNumberFormat: column.useNumberFormat,
        prefix: column.prefix,
        suffix: column.suffix,
        decimalNumbers: column.decimalNumbers,
        show: true,
      }));

      const data = dataResponse.data.map((cells) => {
        const initialValueAcc = {};
        const row = cells.reduce((acc, value, key) => {
          acc[dataResponse.columns[key]] = value;
          return acc;
        }, initialValueAcc);
        return row;
      });

      return JSON.stringify({
        name: "TableComponent",
        props: {
          metadata,
          data,
          alternatingRowColor: configResponse.tableConfig.alternatingRowColor,
          fitColumns: configResponse.tableConfig.fitColumns,
          thresholdValues: configResponse.thresholdValues,
        },
      });
    } catch (error) {
      console.error(`Error loading table data for panel ${panel.id}:`, error);
      // En caso de error, devolver TableComponent sin props
      return JSON.stringify({
        name: "TableComponent",
        props: {},
      });
    }
  }

  if (panel.panelType === "XY Series" && panel.datasourceId) {
    try {
      const configResponse = await getXyChartConfigurationByPanelId(panel.id);
      const dataResponse = await getConsumeDataByPanelId(panel.id);
      // Enviar la configuración completa y los datos al XyChart
      return JSON.stringify({
        name: "XyChart",
        props: {
          configuration: {
            ...configResponse,
            data: dataResponse.data,
            columns: dataResponse.columns,
          },
          isPreview: false,
        },
      });
    } catch (error) {
      console.error(`Error loading xy data for panel ${panel.id}:`, error);
      return JSON.stringify({
        name: "XyChart",
        props: {
          configuration: {
            data: [],
            columns: [],
          },
          isPreview: false,
        },
      });
    }
  }

  if (panel.panelType === "KPI" && panel.datasourceId) {
    try {
      // Llamado a servicios para KPI
      const configResponse = await getKpiConfigurationByPanelId(panel.id);
      const dataResponse = await getConsumeDataByPanelId(panel.id);

      const data = dataResponse.data.map((row) => {
        const obj = {};
        dataResponse.columns.forEach((column, index) => {
          obj[column] = row[index];
        });
        return obj;
      });

      // Transform comparison data if it exists
      let auxComparisonData = null;
      if (dataResponse.auxComparation && dataResponse.auxComparation.data) {
        auxComparisonData = dataResponse.auxComparation.data.map((row) => {
          const obj = {};
          dataResponse.auxComparation.columns.forEach((column, index) => {
            obj[column] = row[index];
          });
          return obj;
        });
      }

      return JSON.stringify({
        name: "KpiChart",
        props: {
          metadata: configResponse,
          data,
          auxComparisonData,
        },
      });
    } catch (error) {
      console.error(`Error loading KPI data for panel ${panel.id}:`, error);
      return JSON.stringify({
        name: "KpiChart",
        props: {
          value: 0,
          label: "KPI",
          auxComparisonData: null,
        },
      });
    }
  }
};

let lastTabFetched = null;

export const getPanelsByTabId = async (tabId) => {
  try {
    // Fetch panels by tab ID
    //if(lastTabFetched == tabId) return;
    let params = new URLSearchParams();
    params.append("tabId", tabId);

    lastTabFetched = tabId;

    const response = await axios.get(environment.getPanelsByTabId + params);

    if (response.data.load.length === 0) {
      return [];
    }

    // Obtener restricciones de tamaño por panelType
    let restrictionsArr = [];
    try {
      restrictionsArr = await fetchPanelSizeRestrictions();
    } catch (e) {
      restrictionsArr = [];
    }
    // Convertir a objeto para acceso rápido
    const restrictionsObj = {};
    restrictionsArr.forEach((r) => {
      restrictionsObj[r.panelType] = {
        minWidth: r.minWidth,
        maxWidth: r.maxWidth,
        minHeight: r.minHeight,
        maxHeight: r.maxHeight,
      };
    });

    // Mapping expected format con await para cada panel
    const panelsWithContent = await Promise.all(
      response.data.load.map(
        async ({ xPos: x, yPos: y, width: w, height: h, panel, id }) => {
          // Si el panel tiene width/height definidos (ya fue modificado en el dashboard), usar esos valores
          // Si NO tiene width/height (es nuevo), usar initialWidth/initialHeight
          const width =
            typeof w === "number" && w > 0 ? w : panel.initialWidth || 50;
          const height =
            typeof h === "number" && h > 0 ? h : panel.initialHeight || 20;

          // Obtener restricciones por panelType
          const restriction = restrictionsObj[panel.panelType] || {};

          return {
            x,
            y,
            w: width,
            h: height,
            id: panel.id,
            tableId: id,
            title: panel.title,
            description: panel.description,
            minWidth: restriction.minWidth,
            maxWidth: restriction.maxWidth,
            minHeight: restriction.minHeight,
            maxHeight: restriction.maxHeight,
            content: await getContentByPanel(panel), // Ahora es async
          };
        }
      )
    );

    return panelsWithContent;
  } catch (error) {
    console.error("Error fetching panels by tab ID:", error);
    throw error; // Re-throw the error for further handling if needed
  }
};

export const createNewTabInWorkstream = async (newTab) => {
  const response = await axios.post(
    environment.createNewTabInWorkstream,
    newTab
  );

  return {
    tabId: response.data.load.id,
    label: response.data.load.tabName,
  };
};

export const updateTabInWorkstream = async (tabData) => {
  const response = await axios.put(environment.updateTabInWorkstream, tabData);

  return {
    tabId: response.data.load.id,
    label: response.data.load.tabName,
    tabDescription: response.data.load.tabDescription,
    showTitleInUppercase: response.data.load.showTitleInUppercase,
  };
};

export const createNewPanelInTabDashboard = async (panelData, activeTabId) => {
  // Obtener los paneles existentes para calcular la posición final
  let lastYPos = 0;
  try {
    const existingPanels = await getPanelsByTabId(activeTabId);
    if (existingPanels.length > 0) {
      // Buscar el panel con mayor y + h
      lastYPos = Math.max(
        ...existingPanels.map((p) =>
          typeof p.y === "number" && typeof p.h === "number" ? p.y + p.h : 0
        )
      );
    }
  } catch (e) {
    lastYPos = 0;
  }

  // Obtener restricciones por panelType
  let restrictionsArr = [];
  try {
    restrictionsArr = await fetchPanelSizeRestrictions();
  } catch (e) {
    restrictionsArr = [];
  }
  const restrictionsObj = {};
  restrictionsArr.forEach((r) => {
    restrictionsObj[r.panelType] = {
      minWidth: r.minWidth,
      maxWidth: r.maxWidth,
      minHeight: r.minHeight,
      maxHeight: r.maxHeight,
    };
  });
  const restriction = restrictionsObj[panelData.panelType] || {};

  const body = {
    title: panelData.title || "New Panel",
    description: panelData.description || "creating new panel",
    panelType: panelData.panelType || null,
    height: panelData.initialHeight || panelData.height || 20,
    width: panelData.initialWidth || panelData.width || 50,
    xPos: panelData.xPos || 0,
    yPos: lastYPos,
    tabId: activeTabId,
    accountId: localStorage.getItem("u_account_id"),
    panelId: panelData.panelId,
    minWidth: restriction.minWidth,
    maxWidth: restriction.maxWidth,
    minHeight: restriction.minHeight,
    maxHeight: restriction.maxHeight,
  };

  const response = await axios.post(environment.createPanelDashboard, body);

  return {
    x: response.data.load.xPos,
    y: response.data.load.yPos,
    w: body.width,
    h: body.height,
    id: response.data.load.panel.id,
    tableId: response.data.load.id,
    minWidth: restriction.minWidth,
    maxWidth: restriction.maxWidth,
    minHeight: restriction.minHeight,
    maxHeight: restriction.maxHeight,
    content: await getContentByPanel(response.data.load.panel),
  };
};

export const updatePanelDashboardPosition = async (panelsToUpdate) => {
  const response = await axios.put(
    environment.updatePanelDashboardPosition,
    panelsToUpdate
  );

  return response.data.load;
};

export const deleteDashboardPanel = async (tableId) => {
  try {
    let params = new URLSearchParams();
    params.append("tabPanelId", tableId);

    const response = await axios.delete(environment.deleteDashboardPanel, {
      params,
    });
    return response.data.load;
  } catch (error) {
    console.error("Error deleting panel:", error);
    throw error;
  }
};

export const deleteDashboardTab = async (tabId) => {
  try {
    const response = await axios.delete(environment.deleteDashboardTab, {
      params: { tabId },
    });
    return response.data.load;
  } catch (error) {
    console.error("Error deleting panel:", error);
    throw error;
  }
};

export const getPredifinedPanelCategories = async (accountId) => {
  try {
    let params = new URLSearchParams();
    params.append("accountId", accountId);

    const response = await axios.get(
      environment.getPredifinedPanelCategories + params
    );

    return response.data.load;
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
};

export const setPredifinedPanelCategorySelectState = async (categoryId) => {
  let params = new URLSearchParams();
  params.append("folderId", categoryId);
  params.append("accountId", localStorage.getItem("u_account_id"));

  const response = await axios.post(
    environment.setPredifinedPanelCategorySelectState + params
  );

  return response.data.load;
};

export const setPredifinedPanelCategoryUnSelectState = async (categoryId) => {
  let params = new URLSearchParams();
  params.append("folderId", categoryId);
  params.append("accountId", localStorage.getItem("u_account_id"));

  const response = await axios.delete(
    environment.setPredifinedPanelCategoryUnSelectState + params
  );

  return response.data.load;
};

export const getPredifinedPanelSubcategoriesByCategoryId = async (
  categoryId
) => {
  try {
    let params = new URLSearchParams();
    params.append("folderId", categoryId);

    const response = await axios.get(
      environment.getPredifinedPanelSubcategoriesByCategoryId + params
    );

    return {
      categoryId,
      subCategories:
        response.data.load.length > 0
          ? response.data.load.map((subcategory) => ({
              ...subcategory,
              categoryId,
            }))
          : [],
    };
  } catch (error) {
    console.error("Error fetching subcategories:", error);
    throw error;
  }
};

export const getPredifinedPanelsByFilter = async (
  page,
  pageSize,
  search,
  panelType,
  body
) => {
  try {
    let params = new URLSearchParams();
    params.append("page", page);
    params.append("pageSize", pageSize);
    params.append("search", search);
    params.append("panelType", panelType);

    const response = await axios.post(
      environment.getPredifinedPanels + params,
      body
    );

    return response.data.load;
  } catch (error) {
    console.error("Error fetching subcategories:", error);
    throw error;
  }
};

export const getTableConfigurationByPanelId = async (panelId) => {
  try {
    let params = new URLSearchParams();
    params.append("panelId", panelId);

    const response = await axios.get(
      environment.getTableConfigurationByPanelId + params
    );
    return response.data.load;
  } catch (error) {
    console.error("Error fetching subcategories:", error);
    throw error;
  }
};

export const getPieConfigurationByPanelId = async (panelId) => {
  try {
    let params = new URLSearchParams();
    params.append("panelId", panelId);

    const response = await axios.get(
      environment.getPieConfigurationByPanelId + params
    );
    return response.data.load;
  } catch (error) {
    console.error("Error fetching pie configuration:", error);
    throw error;
  }
};

export const getConsumeDataByPanelId = async (
  panelId,
  body = {
    search: "",
    page: 0,
    pageSize: 300,
    orderBy: "",
    filters: [],
  }
) => {
  try {
    let params = new URLSearchParams();
    params.append("panelId", panelId);
    const response = await axios.post(
      environment.getConsumeDataByPanelId + params,
      body
    );
    return response.data.load;
  } catch (error) {
    console.error("Error fetching consume data:", error);
    throw error;
  }
};

export const getConsumeDataByPanelIdWithFilters = async (
  panelId,
  filters = []
) => {
  try {
    let params = new URLSearchParams();
    params.append("panelId", panelId);
    const response = await axios.post(
      environment.getConsumeDataByPanelId + params,
      {
        search: "",
        page: 0,
        pageSize: 300,
        orderBy: "",
        filters: filters,
      }
    );
    return response.data.load;
  } catch (error) {
    console.error("Error fetching consume data with filters:", error);
    throw error;
  }
};

export const getConsumeDataByFilterId = async (filterId) => {
  try {
    let params = new URLSearchParams();
    params.append("filterId", filterId);

    const response = await axios.post(
      environment.getConsumeDataByFilterId + params,
      {
        search: "",
        page: 0,
        pageSize: 300,
        orderBy: "",
        filters: [],
      }
    );
    return response.data.load;
  } catch (error) {
    console.error("Error fetching consume data by filter ID:", error);
    throw error;
  }
};

export const getXyChartConfigurationByPanelId = async (panelId) => {
  try {
    let params = new URLSearchParams();
    params.append("panelId", panelId);

    const response = await axios.get(
      environment.getXyChartConfigurationByPanelId + params
    );
    return response.data.load;
  } catch (error) {
    console.error("Error fetching xy chart configuration:", error);
    throw error;
  }
};

export const getKpiConfigurationByPanelId = async (panelId) => {
  try {
    let params = new URLSearchParams();
    params.append("panelId", panelId);

    const response = await axios.get(
      environment.getKpiConfigurationByPanelId + params
    );
    return response.data.load;
  } catch (error) {
    console.error("Error fetching KPI configuration:", error);
    throw error;
  }
};

export const getControlCenter = async (accountId, dashboardId, tabId) => {
  try {
    let params = new URLSearchParams();
    params.append("accountId", accountId);
    params.append("dashboardId", dashboardId);
    params.append("tabId", tabId);

    const response = await axios.get(
      environment.getPanelControlCenter + params
    );
    return response.data.load;
  } catch (error) {
    console.error("Error fetching control center:", error);
    throw error;
  }
};

export const getFilterByControlGroup = async (centerFilterGroupId) => {
  try {
    let params = new URLSearchParams();
    params.append("centerFilterGroupId", centerFilterGroupId);

    const response = await axios.get(
      environment.getFiltersByControlCenter + params
    );
    return response.data.load;
  } catch (error) {
    console.error("Error fetching control center:", error);
    throw error;
  }
};

export const getSaveFilters = async (
  controlCenterId,
  userId,
  accountId,
  tabId
) => {
  try {
    let params = new URLSearchParams();
    params.append("controlCenterId", controlCenterId);
    params.append("userId", userId);
    params.append("accountId", accountId);
    params.append("tabId", tabId);
    const response = await axios.get(
      environment.getSaveControlFilters + params
    );
    return response.data.load;
  } catch (error) {
    console.error("Error fetching control center:", error);
    throw error;
  }
};

export const saveControlFilters = async (
  accountId,
  tabId,
  centerFilterId,
  userId,
  filters
) => {
  try {
    console.log("saveControlFilters - Parámetros recibidos:", {
      accountId,
      tabId,
      centerFilterId,
      userId,
      filters,
    });

    // Validar que filters no esté vacío
    if (!filters || filters.length === 0) {
      throw new Error("El array de filtros no puede estar vacío");
    }

    const payload = {
      accountId: accountId,
      tabId: tabId,
      centerFilterId: centerFilterId,
      userId: userId,
      filters: filters.map((filter) => ({
        ordering: filter.ordering,
        selectionTimeMode: filter.selectionTimeMode,
        value: filter.value,
        displayValue: filter.displayValue,
        filterCompositionId: filter.filterCompositionId,
      })),
    };

    console.log("saveControlFilters - Payload a enviar:", payload);

    const response = await axios.post(environment.saveControlFilters, payload);
    return response.data.load;
  } catch (error) {
    console.error("Error saving control filters:", error);
    console.error("Response data:", error.response?.data);
    throw error;
  }
};
